import { Button, Dropdown, Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { Plus } from "lucide-react"

import { useFetchUsersQuery } from "../../app/adminApiSlice"
import Spinner from "../../components/Spinner"
import DisplayErrorMessage from "../../components/DisplayErrorMessage"
import filterBySearch from "../../utils/filterBySearch"
import filterBySortBy from "../../utils/filterBySortBy"
import MembersList from "./components/MembersList"
import AccountsList from "./components/AccountsList"

const ViewMembersPage = () => {
  const navigate = useNavigate()

  const { data, isSuccess, isLoading, isError, error } = useFetchUsersQuery()

  const [sortedBy, setSortedBy] = useState("")
  const [searchBy, setSearchBy] = useState("")
  const [reverseSort, setReverseSort] = useState(false)
  const [showAccounts, setShowAccounts] = useState(false)

  const handleCreateAccount = () => navigate("/admin/members/create-account")

  const handleSortBy = (sortBy) => {
    if (sortedBy === sortBy) {
      setReverseSort(!reverseSort)
    } else {
      setSortedBy(sortBy)
      setReverseSort(false)
    }
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "Error: An unknown error occurred while fetching users")
    return <DisplayErrorMessage>Error fetching members</DisplayErrorMessage>
  }
  if (!isSuccess || !data) return null

  // Split data into complete members and incomplete accounts
  const completeMembers = Object.entries(data)
    .filter(([_, userData]) => userData.memberDetailsComplete === true)
    .map(([id, userData]) => ({
      id,
      ...userData,
    }))

  const incompleteAccounts = Object.entries(data)
    .filter(([_, userData]) => !userData.memberDetailsComplete)
    .map(([id, userData]) => ({
      id,
      ...userData,
    }))

  // Apply search and sort filters
  const dataToDisplay = showAccounts ? incompleteAccounts : completeMembers

  // Use different search logic for accounts (email/id) vs members (name)
  const searchData = showAccounts
    ? dataToDisplay.filter((account) => {
        const searchLower = searchBy.toLowerCase()
        const emailMatch = account.email?.toLowerCase().includes(searchLower)
        const idMatch = account.id?.toLowerCase().includes(searchLower)
        return emailMatch || idMatch
      })
    : filterBySearch(searchBy, dataToDisplay)

  const sortedData = filterBySortBy(sortedBy, searchData, reverseSort)

  return (
    <>
      <h3 className="mb-4 text-start w-100 fw-bold">
        {showAccounts ? "Incomplete Accounts" : "Members List"}
      </h3>
      {/* BUTTONS */}
      <div className="d-flex flex-wrap gap-3 w-100 mb-2">
        {/* Search bar */}
        <Form style={{ width: "200px" }}>
          <Form.Control
            className="border-0 shadow-sm fw-semibold rounded-3"
            type="text"
            placeholder="Search"
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
          />
        </Form>

        {/* Sort by */}
        <Dropdown>
          <Dropdown.Toggle variant="" className="bg-white border-0 shadow-sm text-muted fw-semibold rounded-3" id="dropdown-basic">
            Sort by
          </Dropdown.Toggle>
          <Dropdown.Menu className="border-0 shadow">
            {showAccounts ? (
              <>
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("email")}>Email</Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => handleSortBy("num")}>GBM Num</Dropdown.Item>
              </>
            ) : (
              <>
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("name")}>Name</Dropdown.Item>
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("email")}>Email</Dropdown.Item>
                <Dropdown.Item as="button" className="mb-1" onClick={() => handleSortBy("num")}>GBM Num</Dropdown.Item>
                <Dropdown.Item as="button" onClick={() => handleSortBy("status")}>Status</Dropdown.Item>
              </>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {/* Create account button */}
        <Button className="d-flex align-items-center gap-2 border-0 rounded-3" variant="dark" onClick={handleCreateAccount}>
          <Plus size={20} color="white" strokeWidth={2} className="align-self-center" />
          Create Account
        </Button>

        {/* Toggle view button */}
        <Button
          className="bg-white border-0 shadow-sm text-dark fw-semibold rounded-3"
          onClick={() => setShowAccounts(!showAccounts)}
        >
          {showAccounts ? "View Members" : "View Incomplete Accounts"}
        </Button>
      </div>

      {/* Table */}
      {showAccounts ? (
        <AccountsList accounts={sortedData} />
      ) : (
        <MembersList members={sortedData} />
      )}
    </>
  )
}

export default ViewMembersPage
